import { Button } from "@shared-components/Button/Button";
import { Container } from "@shared-components/Container/Container";
import { Heading } from "@shared-components/Heading/Heading";
import React, { useEffect, useRef } from "react";
import { DICTIONARY_MAP } from "../../../carlsberg-headless/dictionary-map";
import { Box, ThemeUIStyleObject, Text as ReactText } from "theme-ui";
import { useSiteContext } from "../../../context/site/useSiteContext";
import { useDictionaryContext } from "../../../context/dictionary/useDictionaryContext";
import { keyframes } from "@emotion/react";

const fadeIn = keyframes({ from: { opacity: 0 }, to: { opacity: 1 } });

const formStyles: ThemeUIStyleObject = {
  display: "flex",
  flexWrap: "nowrap",
  justifyContent: "center",
  boxSizing: "border-box",
  color: "darkGreen",
  span: {
    position: "relative",
    "&:focus-within:after": {
      position: "absolute",
      content: "''",
      border: "none",
      borderTopWidth: [2, 3, 4],
      borderTopColor: "white",
      width: ["1.5rem", "2rem", "3rem"],
      borderTopStyle: "solid",
      bottom: ["0.5rem", "1rem", "1.5rem"],
      left: 0,
      right: 0,
      margin: "auto",
      animation: `${fadeIn} 0.8s ease-in-out alternate infinite`,
    },
  },
  input: {
    width: ["1.5rem", "2rem", "3rem"],
    height: ["2.5rem", "3rem", "5rem"],
    padding: [1, 2, 4],
    margin: ["2px", 1, 2],
    background: "transparent",
    borderStyle: "solid",
    borderWidth: [2, 3, 4],
    borderColor: "copper",
    fontSize: ["1rem", "h4", "h2"],
    fontWeight: "bold",
    textAlign: "center",
    fontFamily: "NyCarlsbergDisplay-Bold",
    lineHeight: 2,
    textTransform: "capitalize",
    caretColor: "transparent",
    color: "white",
    appearance: "none",
    "-moz-appearance": "textfield",
    "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
      appearance: "none",
    },
    "&:focus": {
      borderColor: "white",
      boxShadow: "none",
    },
  },
};

const formLineStyles: ThemeUIStyleObject = {
  height: 0,
  width: ["4px", "4px", "12px"],
  minWidth: ["2px", "2px", "4px"],
  border: 0,
  borderTopStyle: "solid",
  borderTopWidth: ["2px", "2px", "4px"],
  borderColor: "copper",
  marginBlock: "auto",
  marginInline: [1, "0.5rem"],
};

const containerStyles: ThemeUIStyleObject = {
  height: "100%",
  backgroundColor: "darkGreen",
  display: "flex",
  flexDirection: "column",
};

const smallContainerStyles: ThemeUIStyleObject = {
  display: "flex",
  paddingTop: 0,
  paddingBottom: 0,
  flexGrow: 1,
  flexDirection: "column",
  justifyContent: "center",
  mt: 14,
};

export const subHeadingStyle: ThemeUIStyleObject = {
  mb: 10,
  fontSize: ["paragraphMedium", "paragraphMedium", "paragraphLarge"],
  color: "white",
  textAlign: "center",
};

export const headingStyle: ThemeUIStyleObject = {
  fontSize: ["h2", "h2", "h1"],
  mb: 10,
  color: "white",
  textAlign: "center",
};

export const CodeModule = () => {
  const FormRef = useRef<HTMLFormElement>(null);
  const inputRef1 = useRef<HTMLInputElement | null>(null);
  const inputRef2 = useRef<HTMLInputElement | null>(null);
  const inputRef3 = useRef<HTMLInputElement | null>(null);
  const inputRef4 = useRef<HTMLInputElement | null>(null);
  const inputRef5 = useRef<HTMLInputElement | null>(null);
  const inputRef6 = useRef<HTMLInputElement | null>(null);
  const inputRef7 = useRef<HTMLInputElement | null>(null);

  const { get } = useDictionaryContext();
  const { currentCulture } = useSiteContext();

  const getScrollbarWidth = () => {
    return window.innerWidth - document.documentElement.clientWidth;
  };

  useEffect(() => {
    const htmlTag = document.documentElement;
    const bodyTag = document.body;
    const scrollBarWidth = getScrollbarWidth();
    htmlTag.style.overflow = "hidden";
    htmlTag.style.position = "fixed";
    htmlTag.style.width = "100%";
    htmlTag.style.marginTop = `-${scrollY}px`;
    bodyTag.style.marginRight = `${scrollBarWidth}px`;
    bodyTag.style.position = "relative";
  }, []);

  const handleSubmit = (e?: any) => {
    e.preventDefault();
    if (
      inputRef1.current?.value !== "" ||
      inputRef2.current?.value !== "" ||
      inputRef3.current?.value !== "" ||
      inputRef4.current?.value !== "" ||
      inputRef5.current?.value !== "" ||
      inputRef6.current?.value !== "" ||
      inputRef7.current?.value !== ""
    ) {
      const theId =
        inputRef1.current?.value.toLocaleUpperCase() +
        "-" +
        inputRef2.current?.value +
        inputRef3.current?.value +
        inputRef4.current?.value +
        "-" +
        inputRef5.current?.value +
        inputRef6.current?.value +
        inputRef7.current?.value;
      window.location.href = window.location + `/#${theId}`;
    }
  };

  return (
    <Box
      id="EntryContainer"
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100svh",
        backgroundColor: "white",
        opacity: "1",
        zIndex: "29",
        overflow: "auto",
        px: [3, 6],
        pt: [3, 6],
        pb: [3, 6],
      }}
    >
      <Box sx={containerStyles}>
        <Container sx={smallContainerStyles}>
          <Container>
            <Heading as={"h2"} sx={headingStyle}>
              {get(
                DICTIONARY_MAP.digital_memories.digital_memories_code_headline,
                currentCulture
              ) || null}
            </Heading>
            <ReactText as={"h3"} sx={subHeadingStyle}>
              {get(
                DICTIONARY_MAP.digital_memories.digital_memories_code_text,
                currentCulture
              ) || null}
            </ReactText>
          </Container>
          <Box as="form" onSubmit={handleSubmit}>
            <Box sx={formStyles} ref={FormRef}>
              <span>
                <input
                  ref={inputRef1}
                  type="text"
                  maxLength={1}
                  autoFocus
                  onInput={(e) => {
                    if (
                      inputRef2.current &&
                      e.currentTarget.value?.match(/[a-z]/i)
                    ) {
                      inputRef2.current.focus();
                    }
                  }}
                  onKeyDown={(e) => {
                    if (inputRef1.current && !e.key?.match(/[a-z]/i)) {
                      e.preventDefault();
                    }
                  }}
                />
              </span>
              <Box sx={formLineStyles} />
              <span>
                <input
                  ref={inputRef2}
                  type="number"
                  maxLength={1}
                  onKeyDown={(e) => {
                    if (!e.key?.match(/[0-9]/i)) {
                      e.preventDefault();
                    }
                  }}
                  onInput={(e) => {
                    if (inputRef3.current) {
                      inputRef3.current.focus();
                    }
                  }}
                  onKeyUp={(e) => {
                    if (e.code == "Backspace" && inputRef1.current) {
                      inputRef1.current.value = "";
                      inputRef1.current.focus();
                    }
                  }}
                  onWheelCapture={(e) => {
                    e.currentTarget.blur();
                    setTimeout(() => {
                      if (inputRef2.current) inputRef2.current.focus();
                    }, 1);
                  }}
                />
              </span>
              <span>
                <input
                  ref={inputRef3}
                  type="number"
                  maxLength={1}
                  onKeyDown={(e) => {
                    if (!e.key?.match(/[0-9]/i)) {
                      e.preventDefault();
                    }
                  }}
                  onInput={(e) => {
                    if (inputRef4.current) {
                      inputRef4.current.focus();
                    }
                  }}
                  onKeyUp={(e) => {
                    if (e.code == "Backspace" && inputRef2.current) {
                      inputRef2.current.value = "";
                      inputRef2.current.focus();
                    }
                  }}
                  onWheelCapture={(e) => {
                    e.currentTarget.blur();
                    setTimeout(() => {
                      if (inputRef3.current) inputRef3.current.focus();
                    }, 1);
                  }}
                />
              </span>
              <span>
                <input
                  ref={inputRef4}
                  type="number"
                  maxLength={1}
                  onKeyDown={(e) => {
                    if (!e.key?.match(/[0-9]/i)) {
                      e.preventDefault();
                    }
                  }}
                  onInput={(e) => {
                    if (inputRef5.current) {
                      inputRef5.current.focus();
                    }
                  }}
                  onKeyUp={(e) => {
                    if (e.code == "Backspace" && inputRef3.current) {
                      inputRef3.current.value = "";
                      inputRef3.current.focus();
                    }
                  }}
                  onWheelCapture={(e) => {
                    e.currentTarget.blur();
                    setTimeout(() => {
                      if (inputRef4.current) inputRef4.current.focus();
                    }, 1);
                  }}
                />
              </span>
              <Box sx={formLineStyles} />
              <span>
                <input
                  ref={inputRef5}
                  type="number"
                  maxLength={1}
                  onKeyDown={(e) => {
                    if (!e.key?.match(/[0-9]/i)) {
                      e.preventDefault();
                    }
                  }}
                  onInput={(e) => {
                    if (inputRef6.current) {
                      inputRef6.current.focus();
                    }
                  }}
                  onKeyUp={(e) => {
                    if (e.code == "Backspace" && inputRef4.current) {
                      inputRef4.current.value = "";
                      inputRef4.current.focus();
                    }
                  }}
                  onWheelCapture={(e) => {
                    e.currentTarget.blur();
                    setTimeout(() => {
                      if (inputRef5.current) inputRef5.current.focus();
                    }, 1);
                  }}
                />
              </span>
              <span>
                <input
                  ref={inputRef6}
                  type="number"
                  maxLength={1}
                  onKeyDown={(e) => {
                    if (!e.key?.match(/[0-9]/i)) {
                      e.preventDefault();
                    }
                  }}
                  onInput={(e) => {
                    if (inputRef7.current) {
                      inputRef7.current.focus();
                    }
                  }}
                  onKeyUp={(e) => {
                    if (e.code == "Backspace" && inputRef5.current) {
                      inputRef5.current.value = "";
                      inputRef5.current.focus();
                    }
                  }}
                  onWheelCapture={(e) => {
                    e.currentTarget.blur();
                    setTimeout(() => {
                      if (inputRef6.current) inputRef6.current.focus();
                    }, 1);
                  }}
                />
              </span>
              <span>
                <input
                  ref={inputRef7}
                  type="number"
                  maxLength={1}
                  onKeyDown={(e) => {
                    if (!e.key?.match(/[0-9]/i)) {
                      e.preventDefault();
                    }
                  }}
                  onKeyUp={(e) => {
                    if (e.code == "Backspace" && inputRef6.current) {
                      inputRef6.current.value = "";
                      inputRef6.current.focus();
                    }
                  }}
                  onWheelCapture={(e) => {
                    e.currentTarget.blur();
                    setTimeout(() => {
                      if (inputRef7.current) inputRef7.current.focus();
                    }, 1);
                  }}
                />
              </span>
            </Box>
            <Button
              type="submit"
              variant="white"
              outline
              sx={{
                display: "block",
                width: "full",
                maxWidth: "max-content",
                margin: "4rem auto",
                ":hover": {
                  span: {
                    color: "black",
                  },
                },
              }}
            >
              {get(
                DICTIONARY_MAP.digital_memories
                  .digital_memories_code_button_text,
                currentCulture
              ) || null}
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};
